import React from "react";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import { SingleValue } from "react-select/dist/declarations/src/types";
import { formatAddressWithStateAbbreviation } from "src/lib/utils";

export type Option = {
  label: string;
  value: google.maps.LatLngLiteral;
  type: "address" | "city" | "state";
  placeId: string;
};

export interface AddressSearchProps {
  initialValue?: google.maps.LatLngLiteral;
  initialOption?: Option;
  onChange?: (value: google.maps.LatLngLiteral | undefined) => void;
  onValueChange?: (value: SingleValue<Option>) => void;
  variant?: "default" | "shadcn" | "dropdown";
  // New configurable parameters
  searchTypes?: string[];
  enableDefaultOptions?: boolean;
  maxResults?: number;
  placeholder?: string;
  useStateAbbreviation?: boolean;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  initialValue,
  initialOption,
  onChange,
  onValueChange,
  variant = "default",
  searchTypes = ["(cities)"],
  enableDefaultOptions = true,
  maxResults = 6,
  placeholder,
  useStateAbbreviation = false,
}) => {
  const { t } = useTranslation();
  const [defaultOptions, setDefaultOptions] = React.useState<Option[]>([]);

  // Helper function to conditionally format address based on prop
  const formatAddressLabel = React.useCallback(
    (address: string) => (useStateAbbreviation ? formatAddressWithStateAbbreviation(address) : address),
    [useStateAbbreviation],
  );

  // Create a stable reference for the initial option
  const stableInitialOption = React.useMemo(() => {
    if (initialOption) {
      return initialOption;
    }
    if (initialValue) {
      return {
        label: JSON.stringify(initialValue),
        value: initialValue,
        placeId: "",
        type: "city" as const,
      };
    }
    return null;
  }, [
    initialOption?.label,
    initialOption?.value?.lat,
    initialOption?.value?.lng,
    initialOption?.placeId,
    initialOption?.type,
    initialValue?.lat,
    initialValue?.lng,
  ]);

  const [locationOption, setLocationOption] = React.useState<Option | null>(stableInitialOption);

  // Update locationOption when stableInitialOption changes
  React.useEffect(() => {
    setLocationOption(stableInitialOption);
  }, [stableInitialOption]);

  // Use Google Places AutocompleteService instead of Geocoder for faster search
  const autocompleteService = React.useMemo(() => new google.maps.places.AutocompleteService(), []);
  const geocoderInstance = React.useMemo(() => new google.maps.Geocoder(), []);
  const sessionToken = React.useMemo(() => new google.maps.places.AutocompleteSessionToken(), []);

  const loadFallbackDefaultOptions = React.useCallback(async () => {
    // Fallback to major US cities
    const majorCities = [
      { name: "New York, NY", lat: 40.7128, lng: -74.006 },
      { name: "Los Angeles, CA", lat: 34.0522, lng: -118.2437 },
      { name: "Chicago, IL", lat: 41.8781, lng: -87.6298 },
      { name: "Houston, TX", lat: 29.7604, lng: -95.3698 },
      { name: "Phoenix, AZ", lat: 33.4484, lng: -112.074 },
      { name: "Philadelphia, PA", lat: 39.9526, lng: -75.1652 },
    ];

    const options: Option[] = majorCities.slice(0, maxResults).map((city) => ({
      label: city.name,
      value: { lat: city.lat, lng: city.lng },
      type: "city",
      placeId: "",
    }));

    setDefaultOptions(options);
  }, [maxResults]);

  const loadDefaultOptions = React.useCallback(
    async (latLng: google.maps.LatLngLiteral) => {
      try {
        // Get the current location's city/state first
        const currentLocationResults = await geocoderInstance.geocode({
          location: latLng,
          componentRestrictions: { country: "us" },
        });

        const allOptions: Option[] = [];

        // Add current location if it's a city or state
        if (currentLocationResults.results.length > 0) {
          const currentLocation = currentLocationResults.results[0];
          if (currentLocation.types.includes("locality") || currentLocation.types.includes("administrative_area_level_1")) {
            allOptions.push({
              label: formatAddressLabel(currentLocation.formatted_address),
              value: {
                lat: currentLocation.geometry.location.lat(),
                lng: currentLocation.geometry.location.lng(),
              },
              type: currentLocation.types.includes("locality") ? "city" : "state",
              placeId: currentLocation.place_id,
            });
          }
        }

        // Get nearby major cities by searching for cities in a radius
        const nearbyCities = [
          { name: "New York, NY", lat: 40.7128, lng: -74.006, placeId: "ChIJOwg_06VPwokRYv534QaPC8g", type: "city" },
          { name: "Los Angeles, CA", lat: 34.0522, lng: -118.2437, placeId: "ChIJE9on3F3HwoAR9AhGJW_fL-I", type: "city" },
          { name: "Chicago, IL", lat: 41.8781, lng: -87.6298, placeId: "ChIJ7cv00DwsDogRAMDACa2m4K8", type: "city" },
          { name: "Houston, TX", lat: 29.7604, lng: -95.3698, placeId: "ChIJAYWNSLS4QIYROwVl894CDco", type: "city" },
          { name: "Phoenix, AZ", lat: 33.4484, lng: -112.074, placeId: "ChIJy3mhUO0SK4cRrBtKNfjHaYw", type: "city" },
          { name: "Philadelphia, PA", lat: 39.9526, lng: -75.1652, placeId: "ChIJ60u11Ni3xokRwVg-jNgU9Yk", type: "city" },
          { name: "San Antonio, TX", lat: 29.4241, lng: -98.4936, placeId: "ChIJrw7QBK9YXIYRvBagEDvhVgg", type: "city" },
          { name: "San Diego, CA", lat: 32.7157, lng: -117.1611, placeId: "ChIJSx6SrQ9T2YARed8V_f0hOg0", type: "city" },
          { name: "Dallas, TX", lat: 32.7767, lng: -96.797, placeId: "ChIJS5dFe_cZTIYRj2dH9qSb7Lk", type: "city" },
          { name: "San Jose, CA", lat: 37.3382, lng: -121.8863, placeId: "ChIJ9T_5iuTKj4ARe3GfygqMnbk", type: "city" },
          { name: "Austin, TX", lat: 30.2672, lng: -97.7431, placeId: "ChIJLwPMoJm1RIYRetVp1EtGm10", type: "city" },
          { name: "Orlando, FL", lat: 30.3322, lng: -81.6557, placeId: "ChIJd7zN_thz54gRnr-lPAaywwo", type: "city" },
        ];

        allOptions.push(
          ...nearbyCities.map((city) => ({
            label: city.name,
            value: { lat: city.lat, lng: city.lng },
            type: city.type as "city" | "state",
            placeId: city.placeId,
          })),
        );

        setDefaultOptions(allOptions);
      } catch (error) {
        console.warn("Error loading default options:", error);
        loadFallbackDefaultOptions();
      }
    },
    [geocoderInstance, maxResults, loadFallbackDefaultOptions, formatAddressLabel],
  );

  // Get user's current location for default options
  React.useEffect(() => {
    if (enableDefaultOptions && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          loadDefaultOptions(userLatLng);
        },
        (error) => {
          console.warn("Could not get user location:", error);
          // Fallback to major US cities if geolocation fails
          loadFallbackDefaultOptions();
        },
      );
    }
  }, [enableDefaultOptions, loadDefaultOptions, loadFallbackDefaultOptions]);

  // Simplified search using Google Places AutocompleteService (like mapInputForm.tsx)
  const searchOptions = React.useCallback(
    (input: string, callback: any) => {
      // Only use Google Places API when there's input
      if (input.length > 0) {
        autocompleteService
          .getPlacePredictions({
            input,
            sessionToken,
            types: searchTypes,
            componentRestrictions: { country: "us" },
            // @ts-ignore
            fields: ["place_id", "formatted_address"],
          })
          .then((res) => {
            if (res.predictions) {
              // Convert predictions to options with lat/lng
              const options = res.predictions.map((item) => ({
                label: formatAddressLabel(item.description),
                value: { lat: 0, lng: 0 }, // Temporary placeholder, will be updated in handleChange
                type: (item.types?.includes("locality") ? "city" : "address") as "address" | "city" | "state",
                placeId: item.place_id,
              }));

              callback(options);
            } else {
              callback([]);
            }
          })
          .catch((error) => {
            console.error("Error in address search:", error);
            callback([]);
          });
      } else {
        // Return default options when no input
        callback(enableDefaultOptions ? defaultOptions : []);
      }
    },
    [sessionToken, searchTypes, enableDefaultOptions, defaultOptions, formatAddressLabel],
  );

  const handleChange = async (value: SingleValue<Option>) => {
    try {
      if (!value) {
        setLocationOption(null);
        onChange?.(undefined);
        onValueChange?.(null);
        return;
      }

      const geocodeResult = await geocoderInstance.geocode({ placeId: value?.placeId ?? "" });
      if (geocodeResult.results.length > 0) {
        const { geometry: { location } } = geocodeResult.results[0];
        const latLngValue = {
          lat: location.lat(),
          lng: location.lng(),
        };

        const newOption = {
          ...value,
          label: formatAddressLabel(value?.label || ""),
          value: latLngValue,
          placeId: value?.placeId ?? "",
        };
        setLocationOption(newOption);
        onChange?.(latLngValue);
        onValueChange?.(newOption);
      }
    } catch (error) {
      console.error("Error converting place_id to coordinates:", error);
    }
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    if (searchTypes.includes("address")) {
      return t("Search address, city, or state");
    }
    return t("Search address");
  };

  if (variant === "shadcn") {
    return (
      <AsyncSelect
        placeholder={getPlaceholder()}
        value={locationOption}
        onChange={handleChange}
        loadOptions={debounce(searchOptions, 500)}
        isClearable
        cacheOptions
        defaultOptions={enableDefaultOptions ? defaultOptions : []}
        className="w-full"
        classNamePrefix="react-select"
        styles={{
          control: (provided, state) => ({
            ...provided,
            borderRadius: "6px",
            border: "1px solid hsl(var(--input))",
            backgroundColor: "hsl(var(--background))",
            padding: "8px 12px",
            fontSize: "12px",
            outline: "none",
            transition: "all 0.2s",
            boxShadow: state.isFocused ? "0 0 0 1px hsl(var(--ring))" : "none",
            "&:hover": {
              borderColor: "hsl(var(--input))",
            },
          }),
          input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
            fontSize: "12px",
            color: "hsl(var(--foreground))",
          }),
          placeholder: (provided) => ({
            ...provided,
            color: "hsl(var(--muted-foreground))",
            fontSize: "12px",
          }),
          singleValue: (provided) => ({
            ...provided,
            color: "hsl(var(--foreground))",
            fontSize: "12px",
          }),
          option: (provided, state) => ({
            ...provided,
            position: "relative",
            display: "flex",
            width: "100%",
            userSelect: "none",
            alignItems: "center",
            borderRadius: "6px",
            padding: "6px 32px 6px 8px",
            fontSize: "12px",
            outline: "none",
            cursor: "pointer",
            backgroundColor: state.isFocused ? "hsl(var(--accent))" : "transparent",
            color: "hsl(var(--foreground))",
            "&:hover": {
              backgroundColor: "hsl(var(--accent))",
            },
          }),
          menu: (provided) => ({
            ...provided,
            maxHeight: "384px",
            minWidth: "8rem",
            overflow: "hidden",
            borderRadius: "6px",
            border: "1px solid hsl(var(--border))",
            backgroundColor: "hsl(var(--popover))",
            color: "hsl(var(--popover-foreground))",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }),
          menuList: (provided) => ({
            ...provided,
            padding: "4px",
            fontSize: "12px",
          }),
          noOptionsMessage: (provided) => ({
            ...provided,
            color: "hsl(var(--muted-foreground))",
            fontSize: "12px",
            padding: "6px 8px",
          }),
          loadingMessage: (provided) => ({
            ...provided,
            color: "hsl(var(--muted-foreground))",
            fontSize: "12px",
            padding: "6px 8px",
          }),
          clearIndicator: (provided) => ({
            ...provided,
            color: "hsl(var(--muted-foreground))",
            cursor: "pointer",
            padding: "4px",
            "& svg": {
              width: "14px",
              height: "14px",
            },
            opacity: 0.5,
            "&:hover": {
              opacity: 1,
            },
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            color: "hsl(var(--muted-foreground))",
            opacity: 0.5,
            padding: "4px",
            "& svg": {
              width: "14px",
              height: "14px",
            },
          }),
          indicatorSeparator: () => ({
            display: "none",
          }),
        }}
      />
    );
  }

  return (
    <AsyncSelect
      className="custom-react-select"
      classNamePrefix="react-select"
      placeholder={getPlaceholder()}
      onChange={handleChange}
      value={locationOption}
      loadingMessage={() => t("Searching for an address")}
      noOptionsMessage={() => t("No address found")}
      defaultOptions={enableDefaultOptions ? defaultOptions : []}
      cacheOptions
      isClearable
      loadOptions={debounce(searchOptions, 500)}
    />
  );
};

export default AddressSearch;
